import { useEffect, useMemo } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { useGetCareBenefitUsageFollowupLazyQuery, CareAccountCreditIntentFragment } from 'shared/types/graphql';

type UseCareBenefitFollowup = {
  id: string;
  followupSteps: UseCareBenefitFollowupStep[];
};

export type UseCareBenefitFollowupStep = {
  id: string;
  description: string;
  completed: boolean;
  displayOrder: number;
  careAccountCreditIntents?: CareAccountCreditIntentFragment[] | null;
};

interface IUseCareBenefitFollowupStepsOutput {
  followup?: UseCareBenefitFollowup;
  loading: boolean;
}

/**
 * Retrieve the first incomplete care benefit followup
 * @param organizationPetId The organization pet id of the pet whose followup we want to retrieve
 * @returns An incomplete care benefit followup
 */
export const useCareBenefitFollowup = (organizationPetId?: string): IUseCareBenefitFollowupStepsOutput => {
  const [getFollowupData, { data, loading }] = useGetCareBenefitUsageFollowupLazyQuery({
    variables: { organizationPetId: organizationPetId || '' },
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });

  useEffect(() => {
    if (organizationPetId) {
      getFollowupData();
    }
  }, [organizationPetId, getFollowupData]);

  const followup = useMemo(
    () => data?.findManyCareBenefitUsageFollowup?.find((followup) => !followup.completed),
    [data?.findManyCareBenefitUsageFollowup],
  );

  return {
    followup,
    loading: loading,
  };
};
